import React, { useEffect, useMemo, useState } from 'react'
import { Card, DefinitionList, PageTitle, StatusBadge, formatDate } from '../components/Ui'
import { adminRequest } from '../api/client'
import { currentAdminCan } from '../data/currentAdminAccess'

const TABS = ['심사·거래 준비', '사업자 정보', '서비스·프로필', '포트폴리오', '변경 이력']

function statusLabel(status) {
  return ({ PENDING:'심사 대기',APPROVED:'승인',REJECTED:'승인 거절',SUSPENDED:'이용 정지',VERIFIED:'확인 완료' })[status] || status || '-'
}

function Checkpoint({ ok, title, description }) {
  return <div className={ok ? 'is-ready' : ''}><span>{ok ? '✓' : '!'}</span><div><strong>{title}</strong><small>{ok ? '확인 가능' : description}</small></div></div>
}

export default function DirectorGovernanceDetail({ id, navigate }) {
  const [director, setDirector] = useState(null)
  const [tab, setTab] = useState('심사·거래 준비')
  const [notice, setNotice] = useState('')
  const [reason, setReason] = useState('')
  const [loading, setLoading] = useState(true)
  const [busy, setBusy] = useState(false)

  const load = async ({ clearNotice = true } = {}) => {
    setLoading(true)
    try {
      const response = await adminRequest(`/api/v1/admin/directors/${encodeURIComponent(id)}`)
      setDirector(response?.data || null)
      if (clearNotice) setNotice('')
    } catch (error) {
      setDirector(null)
      setNotice(error?.message || '디렉터 심사 정보를 불러오지 못했습니다.')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => { load() }, [id])

  const activeServices = useMemo(() => (director?.services || []).filter((item) => item.status === 'ACTIVE'), [director])
  const readiness = useMemo(() => ({
    business: Boolean(director?.businessDocumentMediaId && director?.registrationNo),
    profile: Boolean((director?.shortHeadline || director?.introduction) && activeServices.length),
    approved: director?.status === 'APPROVED'
  }), [director, activeServices])
  const canProcess = currentAdminCan(`/directors/${id}`, 'approve')

  const changeStatus = async (status) => {
    if (!director || busy) return
    if (!canProcess) {
      setNotice('현재 관리자 계정에는 디렉터 승인·처리 권한이 없습니다. 조회만 가능합니다.')
      return
    }
    const memo = reason.trim()
    if ((status === 'SUSPENDED' || status === 'REJECTED') && !memo) {
      window.alert(status === 'REJECTED' ? '승인 거절 사유를 입력해주세요.' : '이용 정지 사유를 입력해주세요.')
      return
    }
    if (status === 'APPROVED' && !readiness.business) {
      window.alert('승인 전에 사업자등록증과 사업자등록번호를 확인해주세요.')
      return
    }
    const previousStatus = director.status
    setBusy(true)
    try {
      await adminRequest(`/api/v1/admin/directors/${encodeURIComponent(id)}/status`, { method:'PATCH', body:{ status, reason:memo || null } })
      setReason('')
      if (status === 'APPROVED') setNotice(previousStatus === 'SUSPENDED' ? '디렉터 이용 정지를 해제했습니다.' : '디렉터 역할을 승인했습니다.')
      else if (status === 'REJECTED') setNotice('디렉터 신청을 거절하고 사유를 기록했습니다.')
      else if (status === 'SUSPENDED') setNotice('디렉터 이용을 정지했습니다.')
      else setNotice('디렉터 신청을 재심사 대기로 변경했습니다.')
      await load({ clearNotice:false })
    } catch (error) {
      setNotice(error?.message || '디렉터 상태를 변경하지 못했습니다.')
    } finally {
      setBusy(false)
    }
  }

  const openDocument = (mediaId) => {
    if (mediaId) window.open(`/api/v1/admin/media/${mediaId}/content`, '_blank', 'noopener')
  }

  const openCustomerPreview = async () => {
    if (!director || director.status !== 'APPROVED') return
    const preview = window.open('about:blank', '_blank')
    if (preview) {
      preview.opener = null
      preview.document.title = 'DIFAN 고객 화면 확인 중'
      preview.document.body.textContent = '실제 공개 디렉터 정보를 확인하고 있습니다.'
    }
    try {
      const response = await adminRequest(`/api/v1/public/directors/${encodeURIComponent(director.id)}`)
      const publicCode = response?.data?.publicCode
      if (!publicCode) throw new Error('공개 디렉터 ID를 확인하지 못했습니다.')
      const target = `https://difan.co.kr/directors/${encodeURIComponent(publicCode)}`
      if (preview) preview.location.replace(target)
      else window.open(target, '_blank', 'noopener')
    } catch (error) {
      if (preview) preview.close()
      setNotice(error?.message || '고객 화면 공개 ID를 확인하지 못했습니다.')
    }
  }

  if (loading && !director) return <><div className="breadcrumb"><button onClick={() => navigate('/director-reviews')}>디렉터 신청 관리</button></div><Card><div className="admin-empty-inline">심사 정보를 불러오는 중입니다.</div></Card></>
  if (!director) return <><div className="breadcrumb"><button onClick={() => navigate('/director-reviews')}>디렉터 신청 관리</button></div><Card><div className="admin-empty-inline"><strong>디렉터를 찾을 수 없습니다.</strong><p>{notice}</p></div></Card></>

  const isPending = director.status === 'PENDING'
  const isApproved = director.status === 'APPROVED'
  const isRejected = director.status === 'REJECTED'
  const isSuspended = director.status === 'SUSPENDED'
  const latestReview = director.reviewHistory?.[0] || null

  return <>
    {notice ? <div className="admin-notice"><strong>안내</strong><span>{notice}</span></div> : null}
    <div className="breadcrumb"><button onClick={() => navigate('/members')}>전체 회원</button>{isPending ? <><span>›</span><button onClick={() => navigate('/director-reviews')}>디렉터 신청 관리</button></> : null}<span>›</span><span>{director.brandName}</span></div>
    <PageTitle eyebrow="DIRECTOR ROLE" title={director.brandName} description={`${director.representativeName || '-'} · 동일한 DIFAN 계정에 추가되는 디렉터 역할을 관리합니다.`} actions={isApproved ? <button type="button" className="button soft" onClick={openCustomerPreview}>고객 화면 미리보기</button> : null} />

    <div className="detail-summary-bar governance-summary">
      <div><span>디렉터 상태</span><StatusBadge status={director.status}>{statusLabel(director.status)}</StatusBadge></div>
      <div><span>사업자 자료</span><StatusBadge status={readiness.business ? 'VERIFIED' : 'PENDING'}>{readiness.business ? '제출 완료' : '확인 필요'}</StatusBadge></div>
      <div><span>서비스·프로필</span><strong>{readiness.profile ? '등록됨' : '확인 필요'}</strong></div>
      <div><span>승인일</span><strong>{director.approvedAt ? formatDate(director.approvedAt, true) : '-'}</strong></div>
    </div>

    <div className="tabs">{TABS.map((item) => <button type="button" key={item} className={tab === item ? 'active' : ''} onClick={() => setTab(item)}>{item}</button>)}</div>

    {tab === '심사·거래 준비' ? <div className="governance-stack">
      <Card title="심사 체크포인트" description="아래 항목은 상태를 확인하기 위한 표시 영역입니다. 카드 자체를 눌러 화면이나 심사 상태가 바뀌지 않습니다.">
        <div className="readiness-admin-grid">
          <Checkpoint ok={readiness.business} title="사업자등록 자료" description="사업자등록증과 등록번호를 확인하세요." />
          <Checkpoint ok={readiness.profile} title="서비스·프로필" description="소개와 활성 서비스 등록 여부를 확인하세요." />
          <Checkpoint ok={readiness.approved} title="디렉터 역할" description={isRejected ? '신청이 승인 거절된 상태입니다.' : isSuspended ? '현재 이용이 정지된 상태입니다.' : '최종 승인 전입니다.'} />
        </div>
      </Card>

      <Card title="디렉터 역할 상태" description="승인·거절·정지·재심사 전환은 아래 명시적 버튼으로만 수행하며 모든 변경은 이력에 남습니다.">
        <div className="governance-status-actions">
          {canProcess ? <>
            <label className="form-field"><span>관리 메모 / 처리 사유</span><textarea rows="3" value={reason} onChange={(event) => setReason(event.target.value)} placeholder={isPending ? '승인 메모 또는 거절 사유 입력' : isApproved ? '이용 정지 시 사유 필수' : isRejected ? '재심사 전환 메모 입력 가능' : '정지 해제 메모 입력 가능'} /></label>
            <div className="row-action-buttons">
              {isPending ? <><button type="button" className="button primary" disabled={busy || !readiness.business} onClick={() => changeStatus('APPROVED')}>{busy ? '처리 중…' : '디렉터 승인'}</button><button type="button" className="button danger-soft" disabled={busy} onClick={() => changeStatus('REJECTED')}>{busy ? '처리 중…' : '승인 거절'}</button></> : null}
              {isApproved ? <button type="button" className="button soft" disabled={busy} onClick={() => changeStatus('SUSPENDED')}>{busy ? '처리 중…' : '이용 정지'}</button> : null}
              {isSuspended ? <button type="button" className="button primary" disabled={busy} onClick={() => changeStatus('APPROVED')}>{busy ? '처리 중…' : '정지 해제'}</button> : null}
              {isRejected ? <button type="button" className="button soft" disabled={busy} onClick={() => changeStatus('PENDING')}>{busy ? '처리 중…' : '재심사로 전환'}</button> : null}
            </div>
          </> : <div className="admin-empty-inline"><strong>조회 전용 권한</strong><p>현재 관리자 계정에는 디렉터 승인·거절·정지·재심사 처리 권한이 없습니다.</p></div>}
          {!readiness.business && isPending ? <p className="field-help">사업자등록증 파일과 사업자등록번호가 있어야 승인할 수 있습니다.</p> : null}
          {isRejected && latestReview?.reason ? <p className="field-help">최근 승인 거절 사유: {latestReview.reason}</p> : null}
        </div>
      </Card>
    </div> : null}

    {tab === '사업자 정보' ? <Card title="사업자 정보" description="신청 시 제출한 사업자 정보와 등록증을 확인합니다.">
      <DefinitionList items={[
        {label:'법적 상호명',value:director.legalName || '-'},
        {label:'사업자등록번호',value:director.registrationNo || '-'},
        {label:'대표자',value:director.ownerName || director.representativeName || '-'},
        {label:'업무용 이메일',value:director.businessEmail || director.email || '-'},
        {label:'업무용 연락처',value:director.businessPhone || director.phone || '-'},
        {label:'사업장 주소',value:director.address || '-'},
        {label:'사업자 확인 상태',value:statusLabel(director.businessStatus)}
      ]} />
      {director.businessDocumentMediaId ? <div className="document-row"><div><span>FILE</span><div><strong>사업자등록증</strong><small>관리자 권한으로만 열람</small></div></div><button type="button" className="button soft" onClick={() => openDocument(director.businessDocumentMediaId)}>문서 보기</button></div> : <div className="admin-empty-inline">사업자등록증이 아직 제출되지 않았습니다.</div>}
    </Card> : null}

    {tab === '서비스·프로필' ? <div className="governance-stack">
      <Card title="기본 프로필"><DefinitionList items={[
        {label:'한줄 소개',value:director.shortHeadline || '-'},
        {label:'소개',value:director.introduction || '-'},
        {label:'서비스 범위',value:director.serviceScope || '-'},
        {label:'진행 방식',value:director.processDescription || '-'},
        {label:'상담 가능 시간',value:director.consultationHours || '-'},
        {label:'준비 기간',value:director.preparationPeriod || '-'}
      ]} /></Card>
      <Card title="활성 서비스"><div className="tag-list">{activeServices.length ? activeServices.map((service) => <span key={service.id}>{service.name}</span>) : <span>등록된 활성 서비스가 없습니다.</span>}</div></Card>
      <Card title="활동 지역"><div className="tag-list">{director.regions?.length ? director.regions.map((region) => <span key={region}>{region}</span>) : <span>등록된 활동 지역이 없습니다.</span>}</div></Card>
    </div> : null}

    {tab === '포트폴리오' ? <Card title="포트폴리오" description={`등록 ${director.portfolioCount || 0}건`}>
      {director.portfolios?.length ? <div className="compact-table-wrap"><table className="data-table compact"><thead><tr><th>제목</th><th>게시 상태</th><th>권리 확인</th><th>등록일</th></tr></thead><tbody>{director.portfolios.map((portfolio) => <tr key={portfolio.id}><td><strong>{portfolio.title}</strong></td><td><StatusBadge status={portfolio.publicationStatus || 'DRAFT'} /></td><td>{portfolio.rightsConfirmed ? '확인' : '미확인'}</td><td>{portfolio.createdAt ? formatDate(portfolio.createdAt, true) : '-'}</td></tr>)}</tbody></table></div> : <div className="admin-empty-inline">등록된 포트폴리오가 없습니다.</div>}
    </Card> : null}

    {tab === '변경 이력' ? <Card title="디렉터 상태 변경 이력">
      {director.reviewHistory?.length ? <div className="activity-feed">{director.reviewHistory.map((item, index) => <div key={`${item.createdAt || index}-${index}`}><time>{item.createdAt ? formatDate(item.createdAt, true) : '-'}</time><span className="activity-type type-admin">A</span><div><strong>{statusLabel(item.fromStatus)} → {statusLabel(item.toStatus)}</strong><p>{item.reason || '사유 없음'}</p></div></div>)}</div> : <div className="admin-empty-inline">상태 변경 이력이 없습니다.</div>}
    </Card> : null}
  </>
}
